import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../../App.css';

/**
 * 汎用ファイルビューアページ
 * 
 * 使い方:
 * navigate('/file-viewer', {
 *   state: {
 *     downloadUrl: '署名付きURL または blob: URL',  // 必須
 *     filename: 'example.mp3',        // オプション（ダウンロード時のファイル名）
 *     mimeType: 'audio/mpeg',         // オプション（ファイルタイプ判定に使用、優先）
 *     title: '音声ファイル再生',       // オプション（ページタイトル）
 *     subtitle: '活動名など',          // オプション（サブタイトル）
 *   }
 * });
 *
 * セキュリティ:
 * - 署名付きURLはバックエンドAPI経由で取得（認証必須）
 * - URLはブラウザのアドレスバーに表示されない
 * - 署名付きURLは1時間で失効
 * - ⚠️ ページリロードでstateは失われる（意図的な設計）
 *
 * downloadUrl が blob: URL の場合（Issue #595）:
 * - 呼び出し元（例: PdfExportButton）が既にメモリ上に生成した Blob を渡すケース
 * - CSP の connect-src（'self' https:）は blob: を許可していないため fetch() での
 *   再取得はブロックされる。この場合は fetch() を行わず、渡された blob: URL を
 *   そのままダウンロードリンクの href として使用する
 * - このケースでは Blob URL の生成・解放責任は呼び出し元にあるため、本コンポーネント
 *   側で revokeObjectURL は行わない（プレビュー表示中の URL を誤って失効させないため）
 */
function FileViewerPage() {
    const location = useLocation();
    const navigate = useNavigate();

    const { downloadUrl, filename, mimeType, title, subtitle } = location.state || {};
    const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const handleBack = () => {
        navigate(-1);
    };

    const handleDownload = async () => {
        if (downloadUrl) {
            setDownloading(true);
            const isBlobUrl = downloadUrl.startsWith('blob:');
            let objectUrl = null;
            try {
                if (isBlobUrl) {
                    // 既に Blob URL のため再取得不要
                    // (CSP の connect-src が blob: を許可していないため fetch() はブロックされる)
                    objectUrl = downloadUrl;
                } else {
                    const response = await fetch(downloadUrl);
                    const blob = await response.blob();
                    objectUrl = URL.createObjectURL(blob);
                }
                const link = document.createElement('a');
                link.href = objectUrl;
                link.download = filename || 'download';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (err) {
                console.error('ダウンロードエラー:', err);
            } finally {
                // blob: URL は呼び出し元が所有・解放を管理するため、ここでは
                // 本コンポーネントが生成した URL (署名付きURLケース) のみ解放する
                if (!isBlobUrl && objectUrl) {
                    URL.revokeObjectURL(objectUrl);
                }
                setDownloading(false);
                setShowDownloadConfirm(false);
            }
        }
    };

    // State がない場合（直接アクセスやリロード）
    if (!downloadUrl) {
        return (
            <div className="file-viewer-container">
                <div className="file-viewer-error">
                    <p className="error-message">ファイルが指定されていません。前の画面から再度アクセスしてください。</p>
                    <button className="file-viewer-back-button" onClick={handleBack}>
                        戻る
                    </button>
                </div>
            </div>
        );
    }

    // ファイルタイプを判定（mimeType優先、なければ拡張子から推測）
    const getFileType = () => {
        // mimeTypeがある場合は優先的に使用
        if (mimeType) {
            if (mimeType.startsWith('audio/')) return 'audio';
            if (mimeType.startsWith('video/')) return 'video';
            if (mimeType.startsWith('image/')) return 'image';
            if (mimeType === 'application/pdf') return 'pdf';
        }

        // フォールバック: 拡張子から判定
        const ext = (filename || '').split('.').pop()?.toLowerCase();
        const audioExts = ['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac'];
        const videoExts = ['mp4', 'webm', 'mov', 'avi'];
        const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
        const pdfExts = ['pdf'];

        if (audioExts.includes(ext)) return 'audio';
        if (videoExts.includes(ext)) return 'video';
        if (imageExts.includes(ext)) return 'image';
        if (pdfExts.includes(ext)) return 'pdf';
        return 'other';
    };

    // ファイルタイプに応じたプレーヤーを表示
    const renderPlayer = () => {
        const fileType = getFileType();

        switch (fileType) {
            case 'audio':
                return (
                    <audio controls src={downloadUrl} className="audio-player">
                        お使いのブラウザは音声再生に対応していません。
                    </audio>
                );
            case 'video':
                return (
                    <video controls src={downloadUrl} className="video-player" style={{ maxWidth: '100%' }}>
                        お使いのブラウザは動画再生に対応していません。
                    </video>
                );
            case 'image':
                return (
                    <img src={downloadUrl} alt={filename} style={{ maxWidth: '100%', borderRadius: '8px' }} />
                );
            case 'pdf':
                return (
                    <iframe
                        src={downloadUrl}
                        title={filename}
                        style={{
                            width: '100%',
                            height: '80vh',
                            border: 'none',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        }}
                    />
                );
            default:
                return (
                    <p style={{ textAlign: 'center', color: '#6b7280' }}>
                        このファイルタイプはプレビューできません。ダウンロードしてご確認ください。
                    </p>
                );
        }
    };

    return (
        <div className="file-viewer-container">
            {/* ダウンロード確認モーダル */}
            {showDownloadConfirm && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3 className="modal-title">ダウンロードの確認</h3>
                        <p style={{ margin: '16px 0', color: '#374151' }}>
                            「{filename}」をダウンロードしますか？
                        </p>
                        <div className="modal-actions">
                            <button className="modal-button-cancel" onClick={() => setShowDownloadConfirm(false)} disabled={downloading}>
                                キャンセル
                            </button>
                            <button className="file-viewer-download-button" onClick={handleDownload} disabled={downloading}>
                                {downloading ? '処理中...' : 'ダウンロード'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="file-viewer-card">
                {/* ヘッダー */}
                <div className="file-viewer-header">
                    <div>
                        <h2>{title || 'ファイル表示'}</h2>
                        {subtitle && <p className="file-viewer-subtitle">{subtitle}</p>}
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button className="file-viewer-back-button" onClick={handleBack}>
                            ← 戻る
                        </button>
                        <button className="file-viewer-download-button" onClick={() => setShowDownloadConfirm(true)}>
                            ダウンロード
                        </button>
                    </div>
                </div>

                {/* プレーヤー */}
                <div className="file-viewer-player">
                    {renderPlayer()}
                    {filename && <p className="file-viewer-filename">{filename}</p>}
                </div>
            </div>
        </div>
    );
}

export default FileViewerPage;
